import type { MeUser } from '~/server/types';

/**
 * The full-page inbox route path.
 */
export const INBOX_URL = '/app/inbox';

/**
 * The URL search param for the selected conversation ID.
 */
export const CONVERSATION_SEARCH_PARAM = 'conversation';

/**
 * The URL search param for the selected company inbox professional URL segment.
 */
export const PROFESSIONAL_SEARCH_PARAM = 'professional';

/**
 * Get the selected conversation ID from the URL search params.
 */
export function getSelectedConversationID(
    searchParams: URLSearchParams,
): number | null {
    const rawValue = searchParams.get(CONVERSATION_SEARCH_PARAM);
    if (!rawValue) {
        return null;
    }
    const conversationID = Number(rawValue);
    return Number.isInteger(conversationID) && conversationID >= 0
        ? conversationID
        : null;
}

/**
 * Get the company inbox professional from the URL search params and the user's
 * professionals. Returns `null` when the param is absent or does not match an
 * inbox the user can access via team membership.
 * @param searchParams The URL search params.
 * @param professionals The user's linked professionals.
 * @returns The company inbox professional from the URL, or `null` if none.
 */
export function getInboxProfessionalFromURL(
    searchParams: URLSearchParams,
    professionals: MeUser['Professionals'],
) {
    const urlSegment = searchParams.get(PROFESSIONAL_SEARCH_PARAM);
    if (!urlSegment) {
        return null;
    }
    return (
        professionals.find(
            (p) => p.URLSegment === urlSegment && p.HasInboxAccess,
        ) ?? null
    );
}

/**
 * Generate the URL for the inbox page. Can have optional inbox and selected conversation.
 * @param professionalURLSegment The company inbox professional URL segment to select, if any.
 * @param conversationID The conversation ID to select, if any.
 * @returns The URL for the inbox page.
 */
export function getInboxURL(
    professionalURLSegment: string | null,
    conversationID: number | null = null,
): string {
    const url = new URL(INBOX_URL, 'https://archipro.co.nz');
    if (professionalURLSegment) {
        url.searchParams.set(PROFESSIONAL_SEARCH_PARAM, professionalURLSegment);
    }
    if (conversationID !== null) {
        url.searchParams.set(CONVERSATION_SEARCH_PARAM, String(conversationID));
    }
    return `${url.pathname}${url.search}`;
}

/**
 * Generate the legacy BM inbox URL for the personal or company inbox.
 * @param professionalURLSegment The company inbox professional URL segment, if any.
 * @param conversationID The conversation ID to open, if any.
 * @returns The legacy inbox URL.
 */
export function getLegacyInboxURL(
    professionalURLSegment: string | null,
    conversationID: number | null = null,
): string {
    const basePath = professionalURLSegment
        ? `/business/${professionalURLSegment}/inbox`
        : '/member/inbox';

    if (conversationID === null || conversationID <= 0) {
        return basePath;
    }

    const url = new URL(basePath, 'https://archipro.co.nz');
    url.searchParams.set('enquiry', String(conversationID));

    return `${url.pathname}${url.search}`;
}

/**
 * Check if the user can open any company inbox via URL segment override.
 * This is only available to staff/high admins.
 */
export function canAdminOverrideCompanyInbox(user: MeUser): boolean {
    return user.IsStaffAdmin || user.IsHighAdmin;
}
